import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '@/components/DataTable';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { EditOnly } from '@/components/EditOnly';
import { Select } from '@/components/Select';
import { CountryMultiSelect } from '@/components/CountryMultiSelect';
import { LanguageMultiSelect } from '@/components/LanguageMultiSelect';
import { COUNTRY_MAP } from '@/lib/countries';
import { formatDateTime } from '@/lib/datetime';
import { LANGUAGE_MAP } from '@/lib/languages';
import {
  getCampaigns, createCampaign, updateCampaign, deleteCampaign, duplicateCampaign,
  type CreateCampaignInput,
} from '@/api/tracker-campaigns';
import { getDomains } from '@/api/tracker-domains';
import { getTrackerPwas } from '@/api/tracker-pwas';
import { getApps } from '@/api/apps';
import { useAuthStore } from '@/store/auth';
import {
  getFlows, createFlow, updateFlow, deleteFlow,
  getFlowOffers, attachOffer, deleteFlowOffer,
} from '@/api/tracker-flows';
import { getOffers } from '@/api/offers';
import { formatOfferLabel } from '@/lib/offers';
import type { TrackerCampaign, TrackerFlow, FlowOffer, CampaignPostback, PostbackLogRow } from '@/api/tracker-types';
import {
  listPostbacks, createPostback, updatePostback, deletePostback, getPostbackLog,
  type PostbackInput,
} from '@/api/tracker-postbacks';

interface CampaignForm {
  app_id: string;
  name: string;
  domain_id: string;
  destination_type: 'redirect' | 'pwa' | 'offer_pool';
  tracker_pwa_id: string;
  fallback_url: string;
  notes: string;
  label: string;
  hide_from_dashboard: boolean;
  fb_pixel_id: string;
  fb_access_token: string;
  fb_test_event_code: string;
  fb_events: Record<string, string>;
}
const initialCampaign: CampaignForm = {
  app_id: '', name: '', domain_id: '', destination_type: 'redirect', tracker_pwa_id: '',
  fallback_url: '', notes: '', label: '', hide_from_dashboard: false,
  fb_pixel_id: '', fb_access_token: '', fb_test_event_code: '',
  fb_events: { pageview: 'PageView', reg: 'CompleteRegistration', dep: 'Purchase' },
};

function useCopy() {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copy = async (text: string, key: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(text);
      else {
        const ta = document.createElement('textarea');
        ta.value = text; ta.style.position = 'fixed'; ta.style.top = '-9999px';
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
    } catch {
      window.prompt('Copy:', text);
    }
  };
  return { copiedKey, copy };
}

export function TrackerCampaigns() {
  const qc = useQueryClient();
  const [mode, setMode] = useState<'create' | 'edit' | null>(null);
  const [selected, setSelected] = useState<TrackerCampaign | null>(null);
  const [form, setForm] = useState<CampaignForm>(initialCampaign);
  const [error, setError] = useState('');
  const [flowsCampaign, setFlowsCampaign] = useState<TrackerCampaign | null>(null);
  const [postbackOpen, setPostbackOpen] = useState(false);
  const { copiedKey, copy } = useCopy();

  const apiBase = (import.meta.env.VITE_API_URL || `${window.location.origin}/api/v1`).replace(/\/+$/, '');
  const postbackTemplates = [
    { key: 'reg',  label: 'Registration', url: `${apiBase}/postback?click_id={click_id}&status=reg&payout=0` },
    { key: 'dep',  label: 'Deposit',      url: `${apiBase}/postback?click_id={click_id}&status=dep&payout={payout}` },
  ];

  const { data: campaigns = [], isLoading } = useQuery({ queryKey: ['tracker-campaigns'], queryFn: getCampaigns });
  const { data: domains = [] } = useQuery({ queryKey: ['tracker-domains'], queryFn: getDomains });
  const { data: pwas = [] } = useQuery({ queryKey: ['tracker-pwas'], queryFn: getTrackerPwas });
  // For a traffic manager a PWA is standalone: any PWA in their scope may back
  // any of their campaigns, so don't narrow the picker by the campaign's app.
  // Everyone else sees the campaign app's PWAs plus PWAs that belong to no app
  // (a traffic manager's) — those clash with no app, so they fit any campaign.
  const isTraffic = useAuthStore((s) => s.admin?.user_type) === 'traffic';
  // An outside client: campaigns stand on their own (the app is optional), on
  // the domains we handed it, with its own PWAs; no offer pools.
  const isClient = useAuthStore((s) => s.isClient());
  const { data: apps = [] } = useQuery({ queryKey: ['apps'], queryFn: getApps });

  const createMut = useMutation({
    mutationFn: (data: CreateCampaignInput) => createCampaign(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tracker-campaigns'] }); close(); },
    onError: (e: Error) => setError(e.message),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TrackerCampaign> }) => updateCampaign(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tracker-campaigns'] }); close(); },
    onError: (e: Error) => setError(e.message),
  });
  const deleteMut = useMutation({
    mutationFn: deleteCampaign,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tracker-campaigns'] }),
  });
  const duplicateMut = useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) => duplicateCampaign(id, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tracker-campaigns'] }),
    onError: (e: Error) => alert(`Duplicate failed: ${e.message}`),
  });

  function close() { setMode(null); setSelected(null); setForm(initialCampaign); setError(''); }
  // A traffic manager's campaign isn't a property of an app, so don't preselect
  // one for them — an empty app is a valid choice.
  function openCreate() { setForm({ ...initialCampaign, app_id: isTraffic || isClient ? '' : apps[0]?.id || '', domain_id: (domains.find((d) => !d.app_id && d.status === 'active') ?? domains[0])?.id || '' }); setError(''); setMode('create'); }
  function openEdit(c: TrackerCampaign) {
    setSelected(c);
    setForm({
      app_id: c.app_id || '', name: c.name, domain_id: c.domain_id, destination_type: c.destination_type,
      tracker_pwa_id: c.tracker_pwa_id || '', fallback_url: c.fallback_url || '', notes: c.notes || '',
      label: c.label || '',
      hide_from_dashboard: !!c.hide_from_dashboard,
      fb_pixel_id: c.fb_pixel_id || '',
      fb_access_token: c.fb_access_token || '',
      fb_test_event_code: c.fb_test_event_code || '',
      fb_events: {
        pageview: 'PageView', reg: 'CompleteRegistration', dep: 'Purchase',
        ...(() => { try { return JSON.parse(c.fb_events_json || '{}'); } catch { return {}; } })(),
      },
    });
    setError(''); setMode('edit');
  }

  // Traffic managers may leave the app empty; for everyone else it is mandatory,
  // except on a campaign that already has none — editing someone else's app-less
  // campaign must not force them to adopt an app for it.
  const appOptional = isTraffic || isClient || (mode === 'edit' && !selected?.app_id);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.domain_id) { setError('Name and domain are required'); return; }
    if (!appOptional && !form.app_id) { setError('App is required'); return; }
    if (form.destination_type === 'pwa' && !form.tracker_pwa_id) { setError('PWA destination requires a tracker PWA'); return; }
    const payload = {
      app_id: form.app_id || null,
      name: form.name.trim(),
      domain_id: form.domain_id,
      destination_type: form.destination_type,
      tracker_pwa_id: form.destination_type === 'pwa' ? form.tracker_pwa_id : null,
      fallback_url: form.fallback_url.trim() || null,
      notes: form.notes.trim() || null,
      label: form.label.trim() || null,
      hide_from_dashboard: form.hide_from_dashboard ? 1 : 0,
      fb_pixel_id: form.fb_pixel_id.trim() || null,
      fb_access_token: form.fb_access_token.trim() || null,
      fb_test_event_code: form.fb_test_event_code.trim() || null,
      fb_events_json: JSON.stringify(form.fb_events),
    };
    if (mode === 'create') createMut.mutate(payload as CreateCampaignInput);
    else if (selected) updateMut.mutate({ id: selected.id, data: payload });
  }

  const domainHostnames = useMemo(() => Object.fromEntries(domains.map((d) => [d.id, d.hostname])), [domains]);
  const appNames = useMemo(() => Object.fromEntries(apps.map((a) => [a.id, a.name])), [apps]);
  const selectedDomain = domains.find((d) => d.id === form.domain_id);
  const trackerUrlPreview = selectedDomain && selected ? `https://${selectedDomain.hostname}/${selected.slug}` : null;

  const labelValues = useMemo(
    () => Array.from(new Set(campaigns.map((c) => c.label).filter((l): l is string => !!l))).sort(),
    [campaigns]
  );

  const columns = [
    { key: 'name', label: 'Name', render: (c: TrackerCampaign) => <span className="font-medium">{c.name}</span> },
    { key: 'app_id', label: 'App', render: (c: TrackerCampaign) => <span className="text-xs">{c.app_id ? appNames[c.app_id] || c.app_id : 'Unassigned'}</span> },
    {
      key: 'link', label: 'Tracker link',
      render: (c: TrackerCampaign) => {
        const host = domainHostnames[c.domain_id] || '?';
        const url = `https://${host}/${c.slug}`;
        return <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-primary text-xs underline">{url}</a>;
      },
    },
    { key: 'destination_type', label: 'Destination', render: (c: TrackerCampaign) => <span className="text-xs">{c.destination_type}</span> },
    { key: 'status', label: 'Status', render: (c: TrackerCampaign) => <span className="text-xs">{c.status}</span> },
    {
      key: 'label', label: 'Label',
      render: (c: TrackerCampaign) => (
        <span className="text-xs">
          {c.label || '—'}
          {!!c.hide_from_dashboard && (
            <span className="ml-2 rounded bg-surface-muted px-1.5 py-0.5 text-[10px] text-content-muted" title="Excluded from the Dashboard">
              no dashboard
            </span>
          )}
          {!isClient && !!c.client_id && (
            <span className="ml-2 rounded bg-warning/15 px-1.5 py-0.5 text-[10px] text-warning" title="A client company's campaign: its traffic stays out of our reports">
              client
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'actions', label: '',
      render: (c: TrackerCampaign) => (
        <div className="flex gap-2">
          {c.destination_type !== 'offer_pool' && (
            <button onClick={(e) => { e.stopPropagation(); setFlowsCampaign(c); }} className="text-xs text-primary hover:underline">Flows</button>
          )}
          <button onClick={(e) => { e.stopPropagation(); openEdit(c); }} className="text-xs text-content-muted hover:underline">Edit</button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              const name = window.prompt('New campaign name:', `${c.name} (copy)`);
              if (name && name.trim()) duplicateMut.mutate({ id: c.id, name: name.trim() });
            }}
            className="text-xs text-info hover:underline"
          >Duplicate</button>
          <button onClick={(e) => { e.stopPropagation(); if (confirm(`Delete ${c.name}? Cascades flows.`)) deleteMut.mutate(c.id); }} className="text-xs text-danger hover:underline">Delete</button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Tracker Campaigns</h1>
          <p className="text-sm text-content-subtle mt-1">Tracker links and their flow configuration</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setPostbackOpen(true)}>Postback URLs</Button>
          <EditOnly><Button onClick={openCreate}>Add campaign</Button></EditOnly>
        </div>
      </div>

      <Modal isOpen={postbackOpen} onClose={() => setPostbackOpen(false)} title="Postback URLs" size="lg">
        <div className="space-y-4">
          <p className="text-sm text-content-muted">
            Paste these into your partner network. Replace <code className="px-1 py-0.5 bg-surface-muted rounded text-xs">{'{click_id}'}</code> and <code className="px-1 py-0.5 bg-surface-muted rounded text-xs">{'{payout}'}</code> with the network's macros.
          </p>
          {postbackTemplates.map((t) => (
            <div key={t.key} className="border border-border rounded-md p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-content-subtle uppercase">{t.label} ({t.key})</span>
                <button
                  type="button"
                  onClick={() => copy(t.url, t.key)}
                  className="text-xs font-medium text-primary hover:text-primary"
                >
                  {copiedKey === t.key ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="font-mono text-xs break-all bg-surface-muted p-2 rounded">{t.url}</div>
            </div>
          ))}
          <div className="text-xs text-content-subtle">
            <div>Required params: <code>click_id</code>, <code>status</code> (<code>reg</code> or <code>dep</code>).</div>
            <div>Optional: <code>payout</code> (USD, e.g. <code>12.5</code>). Same template works for every campaign — the click_id identifies the source.</div>
          </div>
        </div>
      </Modal>

      <DataTable columns={columns} data={campaigns} loading={isLoading} keyExtractor={(c) => c.id} onRowClick={openEdit} />

      <Modal isOpen={mode !== null} onClose={close} title={mode === 'create' ? 'Add tracker campaign' : 'Edit tracker campaign'} size="2xl">
        <form onSubmit={submit} className="space-y-5">
          {error && <div className="p-3 bg-danger-soft text-sm text-danger rounded">{error}</div>}

          {trackerUrlPreview && (
            <div className="bg-primary/10 border border-primary/30 rounded-lg px-3 py-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[11px] font-medium text-primary uppercase tracking-wide">Tracker link</div>
                <a href={trackerUrlPreview} target="_blank" rel="noopener noreferrer" className="block font-mono text-sm text-primary truncate hover:underline">
                  {trackerUrlPreview}
                </a>
              </div>
              <button type="button" onClick={() => copy(trackerUrlPreview, 'tracker-link')} className="text-xs font-medium text-primary hover:text-primary shrink-0">
                {copiedKey === 'tracker-link' ? 'Copied' : 'Copy'}
              </button>
            </div>
          )}

          <Section title="Basics">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="App" required={!appOptional} hint={appOptional ? 'Optional — leave empty if the campaign belongs to no app.' : undefined}>
                <Select
                  value={form.app_id}
                  options={[
                    ...(appOptional ? [{ value: '', label: '— no app —' }] : []),
                    ...apps.map((a) => ({ value: a.id, label: a.bundle_id ? `${a.name} — ${a.bundle_id}` : a.name })),
                  ]}
                  onChange={(v) => setForm({ ...form, app_id: v })}
                  placeholder="— select —"
                  className="w-full"
                />
              </Field>
              <Field label="Name" required>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="e.g. iGaming Tier-1" />
              </Field>
              <Field label="Domain" required hint={domains.filter((d) => d.status !== 'active').length > 0 ? `${domains.filter((d) => d.status !== 'active').length} domain(s) pending` : undefined}>
                <Select
                  value={form.domain_id}
                  options={domains.filter((d) => d.status === 'active').map((d) => ({ value: d.id, label: d.app_id ? `${d.hostname} — ${d.app_name ?? 'app'}'s own` : d.hostname }))}
                  onChange={(v) => setForm({ ...form, domain_id: v })}
                  placeholder="— select —"
                  className="w-full"
                />
              </Field>
            </div>
          </Section>

          <Section title="Destination">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Type" required>
                <Select
                  value={form.destination_type}
                  options={[
                    { value: 'redirect', label: 'Redirect (direct to offer)' },
                    { value: 'pwa', label: 'PWA (show store landing)' },
                    ...(isClient ? [] : [{ value: 'offer_pool', label: 'Offer pool (auto split by GEO)' }]),
                  ]}
                  onChange={(v) => setForm({ ...form, destination_type: v as 'redirect'|'pwa'|'offer_pool' })}
                  className="w-full"
                />
              </Field>
              {form.destination_type === 'pwa' && (
                <Field label="Tracker PWA" required>
                  <Select
                    value={form.tracker_pwa_id}
                    options={pwas.filter((p) => isTraffic || isClient || !p.app_id || p.app_id === form.app_id).map((p) => ({ value: p.id, label: p.name }))}
                    onChange={(v) => setForm({ ...form, tracker_pwa_id: v })}
                    placeholder="— select —"
                    className="w-full"
                  />
                </Field>
              )}
            </div>
            <Field label="Fallback URL" hint="Used when no flow matches or the chosen flow has no offers.">
              <input type="url" value={form.fallback_url} onChange={(e) => setForm({ ...form, fallback_url: e.target.value })} className={inputCls} placeholder="https://..." />
            </Field>
          </Section>

          <Section title="Label">
            <input
              type="text"
              list="campaign-label-options"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              className={inputCls}
              placeholder="e.g. plinko, slots — pick existing or type new"
            />
            <datalist id="campaign-label-options">
              {labelValues.map((l) => <option key={l} value={l} />)}
            </datalist>
          </Section>

          {!isClient && <Section title="Dashboard">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.hide_from_dashboard}
                onChange={(e) => setForm({ ...form, hide_from_dashboard: e.target.checked })}
                className="mt-0.5"
              />
              <span className="text-sm">
                Hide from the Dashboard and Telegram
                <span className="block text-xs text-content-muted">
                  Its clicks, registrations and deposits leave every number on the home Dashboard,
                  and its regs and deposits stop reaching Telegram. Tracker Overview, Clicks and
                  Conversions still show the campaign, and unchecking this brings its full history
                  back.
                </span>
              </span>
            </label>
          </Section>}

          <Section title="Facebook Pixel">
            <Field
              label="Pixel ID"
              hint={form.destination_type === 'pwa'
                ? 'Overrides the pixel set on the tracker PWA. Leave empty to use the PWA’s own.'
                : 'Server-side only: reg/dep go to the Conversions API, plus a PageView on each click with an fbclid.'}
            >
              <input type="text" value={form.fb_pixel_id} onChange={(e) => setForm({ ...form, fb_pixel_id: e.target.value })} className={inputCls} placeholder="e.g. 1234567890" />
            </Field>
            <Field label="Access Token (Conversions API)" hint="Without it no server-side event is sent.">
              <input type="password" autoComplete="off" value={form.fb_access_token} onChange={(e) => setForm({ ...form, fb_access_token: e.target.value })} className={inputCls} placeholder="EAAB..." />
            </Field>
            <Field label="Test Event Code" hint="Optional — for Facebook Events Manager test traffic.">
              <input type="text" value={form.fb_test_event_code} onChange={(e) => setForm({ ...form, fb_test_event_code: e.target.value })} className={inputCls} placeholder="TEST12345" />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              {(form.destination_type === 'pwa' ? ['reg', 'dep'] : ['pageview', 'reg', 'dep']).map((k) => (
                <div key={k}>
                  <label className="block text-xs text-gray-500 mb-1">{k} event name</label>
                  <input
                    type="text"
                    value={form.fb_events[k] ?? ''}
                    onChange={(e) => setForm({ ...form, fb_events: { ...form.fb_events, [k]: e.target.value } })}
                    className={inputCls}
                    placeholder="(empty = off)"
                  />
                </div>
              ))}
            </div>
          </Section>

          <Section title="Notes">
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} resize-y`} placeholder="Internal notes for this campaign" />
          </Section>

          {mode === 'edit' && selected && (
            <OutgoingPostbacksSection campaignId={selected.id} />
          )}

          {mode === 'edit' && selected && (
            <Section title="Postback URLs" subtitle="Paste these into your partner network">
              <div className="space-y-2">
                {postbackTemplates.map((t) => (
                  <div key={t.key} className="flex items-center gap-2 bg-surface-muted border border-border rounded-md px-3 py-2">
                    <span className="text-[11px] font-semibold uppercase text-content-subtle w-12 shrink-0">{t.key}</span>
                    <span className="font-mono text-xs break-all flex-1 text-content-muted">{t.url}</span>
                    <button
                      type="button"
                      onClick={() => copy(t.url, `inline-${t.key}`)}
                      className="text-xs font-medium text-primary hover:text-primary shrink-0"
                    >
                      {copiedKey === `inline-${t.key}` ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                ))}
              </div>
            </Section>
          )}

          <div className="flex justify-between items-center gap-3 pt-3 border-t border-border">
            {mode === 'edit' && selected ? (
              <button
                type="button"
                onClick={() => {
                  if (confirm(`Delete ${selected.name}? Cascades flows.`)) {
                    deleteMut.mutate(selected.id);
                    close();
                  }
                }}
                className="text-sm text-danger hover:underline"
              >
                Delete campaign
              </button>
            ) : <span />}
            <div className="flex items-center gap-3">
              <Button type="button" variant="secondary" onClick={close}>Cancel</Button>
              <Button type="submit" loading={createMut.isPending || updateMut.isPending}>{mode === 'create' ? 'Create' : 'Save'}</Button>
            </div>
          </div>
        </form>
      </Modal>

      {flowsCampaign && <FlowsPanel campaign={flowsCampaign} domainHostname={domainHostnames[flowsCampaign.domain_id] || ''} onClose={() => setFlowsCampaign(null)} />}
    </div>
  );
}

// ----- shared form helpers -----

const inputCls = 'block w-full px-3 py-2 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary';

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="bg-surface border border-border rounded-lg p-4 space-y-3">
      <div>
        <div className="text-sm font-semibold text-content">{title}</div>
        {subtitle && <div className="text-xs text-content-subtle mt-0.5">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, required, hint, children }: { label: string; required?: boolean; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-content-muted mb-1">
        {label}{required && <span className="text-danger ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-content-subtle">{hint}</p>}
    </div>
  );
}

// ============================================================================
// Flows master/detail panel
// ============================================================================

function FlowsPanel({ campaign, domainHostname, onClose }: { campaign: TrackerCampaign; domainHostname: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: flows = [] } = useQuery({ queryKey: ['tracker-flows', campaign.id], queryFn: () => getFlows(campaign.id) });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Auto-select first flow whenever the list changes & current is gone
  useEffect(() => {
    if (flows.length === 0) { setSelectedId(null); return; }
    if (!selectedId || !flows.find((f) => f.id === selectedId)) {
      setSelectedId(flows[0].id);
    }
  }, [flows, selectedId]);

  const trackerUrl = domainHostname ? `https://${domainHostname}/${campaign.slug}` : '';

  const createMut = useMutation({
    mutationFn: () => createFlow({
      campaign_id: campaign.id,
      name: 'New flow',
      priority: (flows[flows.length - 1]?.priority ?? 90) + 10,
      filters_json: '{}',
    }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['tracker-flows', campaign.id] });
      setSelectedId(created.id);
    },
  });

  const deleteMut = useMutation({
    mutationFn: deleteFlow,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tracker-flows', campaign.id] }),
  });

  const selected = flows.find((f) => f.id === selectedId) || null;

  return (
    <Modal isOpen={true} onClose={onClose} title={`Flows · ${campaign.name}`} size="full">
      <div className="flex flex-col sm:flex-row gap-4 h-full min-h-0">
        {/* Left: flow list */}
        <aside className="sm:w-[300px] sm:flex-shrink-0 sm:border-r sm:border-border sm:pr-4 flex flex-col min-h-0">
          {trackerUrl && (
            <div className="text-[11px] text-content-subtle mb-2">
              <div className="font-medium text-content-muted uppercase tracking-wide mb-0.5">Tracker link</div>
              <a href={trackerUrl} target="_blank" rel="noopener noreferrer" className="font-mono text-primary hover:underline break-all">{trackerUrl}</a>
            </div>
          )}

          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-content">Flows ({flows.length})</div>
            <Button variant="secondary" onClick={() => createMut.mutate()} loading={createMut.isPending}>+ New</Button>
          </div>

          <div className="flex-1 overflow-y-auto space-y-1 -mx-1 px-1">
            {flows.length === 0 && (
              <p className="text-xs text-content-subtle text-center py-6">No flows yet. Click "+ New" to create one.</p>
            )}
            {flows.map((f) => (
              <FlowListItem key={f.id} flow={f} active={f.id === selectedId} onSelect={() => setSelectedId(f.id)} />
            ))}
          </div>
        </aside>

        {/* Right: detail */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {selected ? (
            <FlowDetail
              key={selected.id}
              flow={selected}
              onDelete={() => {
                if (confirm(`Delete flow "${selected.name}"?`)) deleteMut.mutate(selected.id);
              }}
            />
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-content-subtle">
              Select a flow on the left, or create a new one.
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end pt-3 mt-3 border-t border-border flex-shrink-0">
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
}

function FlowListItem({ flow, active, onSelect }: { flow: TrackerFlow; active: boolean; onSelect: () => void }) {
  const filters = useMemo(() => {
    try { return JSON.parse(flow.filters_json || '{}') as Record<string, unknown>; } catch { return {}; }
  }, [flow.filters_json]);
  const countries = Array.isArray(filters.country) ? (filters.country as string[]) : [];
  const languages = Array.isArray(filters.language) ? (filters.language as string[]) : [];
  const noFilters = countries.length === 0 && languages.length === 0
    && Object.keys(filters).filter((k) => k !== 'country' && k !== 'language').length === 0;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left p-2.5 rounded-md border transition-colors ${
        active ? 'border-primary bg-primary/5' : 'border-border hover:bg-surface-muted'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-sm text-content truncate">{flow.name}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium uppercase shrink-0 ${
          flow.status === 'active' ? 'bg-success-soft text-success' : 'bg-surface-muted text-content-muted'
        }`}>{flow.status}</span>
      </div>
      <div className="text-[11px] text-content-subtle mt-0.5">priority {flow.priority}</div>
      <div className="flex flex-wrap gap-1 mt-1.5">
        {countries.slice(0, 4).map((c) => (
          <span key={c} className="inline-flex items-center gap-0.5 text-[10px] bg-surface-muted text-content-muted px-1 py-px rounded">
            <span>{COUNTRY_MAP[c]?.flag || '🌍'}</span>{c}
          </span>
        ))}
        {countries.length > 4 && <span className="text-[10px] text-content-subtle">+{countries.length - 4}</span>}
        {languages.slice(0, 3).map((l) => (
          <span key={l} className="text-[10px] bg-info-soft text-info px-1 py-px rounded font-mono uppercase">{l}</span>
        ))}
        {noFilters && <span className="text-[10px] text-content-subtle italic">all traffic</span>}
      </div>
    </button>
  );
}

function FlowDetail({ flow, onDelete }: { flow: TrackerFlow; onDelete: () => void }) {
  const qc = useQueryClient();
  const [error, setError] = useState('');

  const initialFilters = useMemo(() => {
    try { return JSON.parse(flow.filters_json) as Record<string, unknown>; } catch { return {}; }
  }, [flow.filters_json]);

  const [name, setName] = useState(flow.name);
  const [priority, setPriority] = useState(flow.priority);
  const [status, setStatus] = useState(flow.status);
  const [countries, setCountries] = useState<string[]>(
    Array.isArray(initialFilters.country) ? (initialFilters.country as string[]) : []
  );
  const [languages, setLanguages] = useState<string[]>(
    Array.isArray(initialFilters.language) ? (initialFilters.language as string[]) : []
  );
  const [otherFiltersJson, setOtherFiltersJson] = useState(() => {
    const rest: Record<string, unknown> = { ...initialFilters };
    delete rest.country; delete rest.language;
    return Object.keys(rest).length > 0 ? JSON.stringify(rest, null, 2) : '';
  });
  const [showAdvanced, setShowAdvanced] = useState(otherFiltersJson.length > 0);

  function buildFiltersJson(): string {
    let rest: Record<string, unknown> = {};
    if (otherFiltersJson.trim()) {
      try {
        rest = JSON.parse(otherFiltersJson);
        if (typeof rest !== 'object' || Array.isArray(rest) || rest === null) throw new Error();
      } catch {
        throw new Error('Advanced filters must be valid JSON object');
      }
    }
    const out: Record<string, unknown> = { ...rest };
    if (countries.length > 0) out.country = countries; else delete out.country;
    if (languages.length > 0) out.language = languages; else delete out.language;
    return JSON.stringify(out);
  }

  const dirty = name.trim() !== flow.name
    || priority !== flow.priority
    || status !== flow.status
    || (() => { try { return buildFiltersJson() !== flow.filters_json; } catch { return true; } })();

  const saveMut = useMutation({
    mutationFn: () => {
      const filters_json = buildFiltersJson();
      return updateFlow(flow.id, { name: name.trim() || flow.name, priority, status, filters_json });
    },
    onSuccess: () => {
      setError('');
      qc.invalidateQueries({ queryKey: ['tracker-flows', flow.campaign_id] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="space-y-4 pb-4">
      {error && <div className="p-2 bg-danger-soft text-xs text-danger rounded">{error}</div>}

      {/* Header: name + status + priority */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-[11px] font-medium text-content-subtle uppercase tracking-wide mb-1">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="block w-full px-3 py-2 border border-border-strong rounded-md text-base font-medium focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-content-subtle uppercase tracking-wide mb-1">Priority</label>
          <input
            type="number"
            value={priority}
            onChange={(e) => setPriority(parseInt(e.target.value) || 0)}
            title="Lower = matched first"
            className="block w-24 px-3 py-2 border border-border-strong rounded-md text-sm"
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-content-subtle uppercase tracking-wide mb-1">Status</label>
          <Select
            value={status}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'paused', label: 'Paused' },
            ]}
            onChange={setStatus}
            className="w-32"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-surface border border-border rounded-lg p-4 space-y-3">
        <div className="text-sm font-semibold text-content">Targeting</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Field label="Countries" hint="Empty = match all countries">
            <CountryMultiSelect value={countries} onChange={setCountries} placeholder="Search countries…" />
          </Field>
          <Field label="Languages" hint="Empty = match all languages">
            <LanguageMultiSelect value={languages} onChange={setLanguages} placeholder="Search languages…" />
          </Field>
        </div>
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="text-xs text-content-subtle hover:text-content-muted"
        >
          {showAdvanced ? '▾ Hide advanced filters' : '▸ Advanced (platform · device · os)'}
        </button>
        {showAdvanced && (
          <div>
            <textarea
              rows={4}
              value={otherFiltersJson}
              onChange={(e) => setOtherFiltersJson(e.target.value)}
              className="block w-full px-3 py-2 border border-border-strong rounded-md text-sm font-mono resize-y"
              placeholder='{"platform":["ios"],"device_type":["phone"]}'
            />
            <p className="mt-1 text-xs text-content-subtle">Optional JSON object. Country & language are edited above.</p>
          </div>
        )}

        {/* Quick filter summary */}
        <FilterSummary countries={countries} languages={languages} />
      </div>

      {/* Offers */}
      <FlowOffersEditor flow={flow} />

      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-border sticky bottom-0 bg-surface">
        <button type="button" onClick={onDelete} className="text-sm text-danger hover:underline">
          Delete flow
        </button>
        <div className="flex items-center gap-3">
          {dirty && <span className="text-xs text-warning">unsaved changes</span>}
          <Button onClick={() => saveMut.mutate()} loading={saveMut.isPending} disabled={!dirty}>Save</Button>
        </div>
      </div>
    </div>
  );
}

function FilterSummary({ countries, languages }: { countries: string[]; languages: string[] }) {
  if (countries.length === 0 && languages.length === 0) {
    return <div className="text-xs text-content-subtle italic">No targeting filters — matches all traffic in this campaign.</div>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {countries.map((c) => {
        const country = COUNTRY_MAP[c];
        return (
          <span key={`c-${c}`} className="inline-flex items-center gap-1 text-xs bg-surface-muted text-content-muted px-1.5 py-0.5 rounded">
            <span>{country?.flag || '🌍'}</span>{c}
          </span>
        );
      })}
      {languages.map((l) => (
        <span key={`l-${l}`} className="inline-flex items-center gap-1 text-xs bg-info-soft text-info px-1.5 py-0.5 rounded">
          <span className="font-mono uppercase">{l}</span>
          {LANGUAGE_MAP[l]?.name && <span>{LANGUAGE_MAP[l].name}</span>}
        </span>
      ))}
    </div>
  );
}

function FlowOffersEditor({ flow }: { flow: TrackerFlow }) {
  const qc = useQueryClient();
  const { data: links = [] } = useQuery({ queryKey: ['flow-offers', flow.id], queryFn: () => getFlowOffers(flow.id) });
  const { data: offers = [] } = useQuery({ queryKey: ['offers'], queryFn: getOffers });
  const [offerToAttach, setOfferToAttach] = useState('');
  const [attachPriority, setAttachPriority] = useState(100);

  const attachMut = useMutation({
    mutationFn: () => attachOffer(flow.id, offerToAttach, attachPriority),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['flow-offers', flow.id] }); setOfferToAttach(''); },
  });
  const detachMut = useMutation({
    mutationFn: deleteFlowOffer,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flow-offers', flow.id] }),
  });

  const offersById = Object.fromEntries(offers.map((o) => [o.id, o]));
  const attachedIds = new Set(links.map((l: FlowOffer) => l.offer_id));
  const availableOffers = offers.filter((o) => !attachedIds.has(o.id));

  const sorted = [...links].sort((a, b) => a.priority - b.priority);

  return (
    <div className="bg-surface border border-border rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-content">Offers</div>
        <span className="text-[11px] text-content-subtle">{links.length} attached · lower priority matched first</span>
      </div>

      {sorted.length === 0 && (
        <p className="text-xs text-content-subtle italic py-2">No offers attached. Attach one below or the campaign's fallback URL will be used.</p>
      )}

      <div className="space-y-1">
        {sorted.map((l: FlowOffer, idx) => {
          const o = offersById[l.offer_id];
          return (
            <div key={l.id} className="flex items-center gap-3 bg-surface-muted border border-border px-3 py-2 rounded-md">
              <span className="text-xs font-mono text-content-subtle w-6 text-right">{idx + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-content truncate">
                  {o ? formatOfferLabel(o) : l.offer_id}
                </div>
                {o?.payout_cents != null && (
                  <div className="text-[11px] text-content-subtle">payout {(o.currency === 'EUR' ? '€' : '$')}{(o.payout_cents / 100).toFixed(2)}</div>
                )}
              </div>
              <span className="text-xs text-content-subtle">prio {l.priority}</span>
              <button onClick={() => detachMut.mutate(l.id)} className="text-xs text-danger hover:underline">Remove</button>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2 items-end pt-2 border-t border-border">
        <div className="flex-1 min-w-[240px]">
          <label className="block text-[11px] font-medium text-content-subtle uppercase tracking-wide mb-1">Attach offer</label>
          <Select
            value={offerToAttach}
            options={availableOffers.map((o) => ({ value: o.id, label: formatOfferLabel(o) }))}
            onChange={setOfferToAttach}
            placeholder={availableOffers.length === 0 ? 'All offers attached' : '— select offer —'}
            searchable
            className="w-full"
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-content-subtle uppercase tracking-wide mb-1">Priority</label>
          <input
            type="number"
            value={attachPriority}
            onChange={(e) => setAttachPriority(parseInt(e.target.value) || 100)}
            className="w-24 px-3 py-2 border border-border-strong rounded-md text-sm"
          />
        </div>
        <Button disabled={!offerToAttach} onClick={() => attachMut.mutate()} loading={attachMut.isPending}>Attach</Button>
      </div>
    </div>
  );
}

// ----- S2S postbacks editor -----

const EVENT_OPTIONS: { value: 'reg' | 'dep'; label: string }[] = [
  { value: 'reg', label: 'Registration' },
  { value: 'dep', label: 'Deposit (FTD)' },
];

type SourceType = 'organic' | 'organic_force' | 'referral_force' | 'referral_click' | 'ad_click';
const SOURCE_OPTIONS: { value: SourceType; label: string }[] = [
  { value: 'organic', label: 'organic' },
  { value: 'organic_force', label: 'organic_force' },
  { value: 'referral_force', label: 'referral_force' },
  { value: 'referral_click', label: 'referral_click' },
  { value: 'ad_click', label: 'ad_click' },
];

const POSTBACK_MACROS = [
  '{click_id}', '{status}', '{payout}', '{payout_cents}', '{currency}',
  '{country}', '{offer_id}', '{campaign_id}', '{flow_id}', '{external_click_id}',
  '{sub_id_1}', '{sub_id_2}', '{sub_id_3}', '{asa}',
];

interface PostbackForm {
  name: string;
  url: string;
  method: 'GET' | 'POST';
  events: ('reg' | 'dep')[];
  sources: SourceType[];
  status: 'active' | 'paused';
}
const emptyPostbackForm: PostbackForm = {
  name: '', url: '', method: 'GET', events: ['reg', 'dep'], sources: [], status: 'active',
};

function parseEvents(json: string): ('reg' | 'dep')[] {
  try {
    const arr = JSON.parse(json);
    if (Array.isArray(arr)) {
      return arr.filter((e): e is 'reg' | 'dep' => e === 'reg' || e === 'dep');
    }
  } catch { /* ignore */ }
  return [];
}

function parseSources(json: string | null | undefined): SourceType[] {
  if (!json) return [];
  try {
    const arr = JSON.parse(json);
    if (Array.isArray(arr)) {
      const allowed = new Set(SOURCE_OPTIONS.map((o) => o.value));
      return arr.filter((e): e is SourceType => typeof e === 'string' && allowed.has(e as SourceType));
    }
  } catch { /* ignore */ }
  return [];
}

function OutgoingPostbacksSection({ campaignId }: { campaignId: string }) {
  const qc = useQueryClient();
  const { data: postbacks = [], isLoading } = useQuery({
    queryKey: ['campaign-postbacks', campaignId],
    queryFn: () => listPostbacks(campaignId),
  });

  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<PostbackForm>(emptyPostbackForm);
  const [logFor, setLogFor] = useState<string | null>(null);
  const [error, setError] = useState('');

  const createMut = useMutation({
    mutationFn: (data: PostbackInput) => createPostback(campaignId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaign-postbacks', campaignId] });
      setEditing(null); setError('');
    },
    onError: (e: Error) => setError(e.message),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PostbackInput> }) => updatePostback(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaign-postbacks', campaignId] });
      setEditing(null); setError('');
    },
    onError: (e: Error) => setError(e.message),
  });
  const deleteMut = useMutation({
    mutationFn: deletePostback,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaign-postbacks', campaignId] }),
  });

  function startEdit(pb: CampaignPostback) {
    setEditing(pb.id);
    setForm({
      name: pb.name,
      url: pb.url,
      method: pb.method,
      events: parseEvents(pb.events_json),
      sources: parseSources(pb.sources_json),
      status: pb.status,
    });
    setError('');
  }
  function startNew() {
    setEditing('new');
    setForm(emptyPostbackForm);
    setError('');
  }
  function cancel() { setEditing(null); setError(''); }

  function save(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.url.trim()) {
      setError('Name and URL are required');
      return;
    }
    if (form.events.length === 0) {
      setError('Pick at least one event');
      return;
    }
    const payload: PostbackInput = {
      name: form.name.trim(),
      url: form.url.trim(),
      method: form.method,
      events: form.events,
      sources: form.sources,
      status: form.status,
    };
    if (editing === 'new') createMut.mutate(payload);
    else if (editing) updateMut.mutate({ id: editing, data: payload });
  }

  function toggleEvent(ev: 'reg' | 'dep') {
    setForm((f) => ({
      ...f,
      events: f.events.includes(ev) ? f.events.filter((x) => x !== ev) : [...f.events, ev],
    }));
  }

  function toggleSource(src: SourceType) {
    setForm((f) => ({
      ...f,
      sources: f.sources.includes(src) ? f.sources.filter((x) => x !== src) : [...f.sources, src],
    }));
  }

  return (
    <Section
      title="S2S Postbacks"
      subtitle="Forward each conversion to one or more downstream URLs (CPA networks, your tracker, …). Failed deliveries are retried with exponential backoff."
    >
      {isLoading ? (
        <div className="text-xs text-content-subtle">Loading…</div>
      ) : postbacks.length === 0 ? (
        <div className="text-xs text-content-subtle">No postbacks configured.</div>
      ) : (
        <div className="space-y-2">
          {postbacks.map((pb) => {
            const events = parseEvents(pb.events_json).join(', ') || '—';
            const srcList = parseSources(pb.sources_json);
            const sourcesLabel = srcList.length === 0 ? 'all' : srcList.join(', ');
            return (
              <div key={pb.id} className="border border-border rounded-md p-3">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-content">{pb.name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-semibold ${pb.status === 'active' ? 'bg-success-soft text-success' : 'bg-surface-muted text-content-subtle'}`}>{pb.status}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-surface-muted text-content-muted font-semibold">{pb.method}</span>
                      <span className="text-[10px] text-content-subtle">events: {events}</span>
                      <span className="text-[10px] text-content-subtle">sources: {sourcesLabel}</span>
                    </div>
                    <div className="font-mono text-[11px] break-all text-content-muted mt-1">{pb.url}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button type="button" className="text-xs text-primary hover:underline" onClick={() => startEdit(pb)}>Edit</button>
                    <button type="button" className="text-xs text-content-muted hover:underline" onClick={() => setLogFor(logFor === pb.id ? null : pb.id)}>{logFor === pb.id ? 'Hide log' : 'Log'}</button>
                    <button type="button" className="text-xs text-danger hover:underline" onClick={() => { if (confirm(`Delete postback "${pb.name}"?`)) deleteMut.mutate(pb.id); }}>Delete</button>
                  </div>
                </div>
                {logFor === pb.id && <PostbackLog postbackId={pb.id} />}
              </div>
            );
          })}
        </div>
      )}

      {editing === null ? (
        <button type="button" onClick={startNew} className="text-sm font-medium text-primary hover:text-primary">
          + Add postback
        </button>
      ) : (
        <div className="border border-primary/30 bg-primary/10/40 rounded-md p-3 space-y-3">
          {error && <div className="p-2 bg-danger-soft text-xs text-danger rounded">{error}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Name" required>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="e.g. Keitaro main" />
            </Field>
            <Field label="Method">
              <Select
                value={form.method}
                options={[{ value: 'GET', label: 'GET' }, { value: 'POST', label: 'POST' }]}
                onChange={(v) => setForm({ ...form, method: v as 'GET' | 'POST' })}
                className="w-full"
              />
            </Field>
            <Field label="Status">
              <Select
                value={form.status}
                options={[{ value: 'active', label: 'Active' }, { value: 'paused', label: 'Paused' }]}
                onChange={(v) => setForm({ ...form, status: v as 'active' | 'paused' })}
                className="w-full"
              />
            </Field>
          </div>
          <Field label="URL" required hint="Use macros below — they are URL-encoded automatically.">
            <input type="url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className={inputCls} placeholder="https://tracker.example.com/postback?subid={click_id}&payout={payout}&status={status}" />
          </Field>
          <div>
            <div className="text-xs font-medium text-content-muted mb-1">Events</div>
            <div className="flex flex-wrap gap-2">
              {EVENT_OPTIONS.map((ev) => {
                const checked = form.events.includes(ev.value);
                return (
                  <label key={ev.value} className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded border cursor-pointer ${checked ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-surface border-border-strong text-content-muted'}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleEvent(ev.value)} className="hidden" />
                    {ev.label}
                  </label>
                );
              })}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-content-muted mb-1">Source type</div>
            <div className="text-[11px] text-content-subtle mb-1.5">
              Leave empty to fire on all sources. Pick one or more to restrict this postback to specific traffic sources.
            </div>
            <div className="flex flex-wrap gap-2">
              {SOURCE_OPTIONS.map((s) => {
                const checked = form.sources.includes(s.value);
                return (
                  <label key={s.value} className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded border cursor-pointer ${checked ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-surface border-border-strong text-content-muted'}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleSource(s.value)} className="hidden" />
                    {s.label}
                  </label>
                );
              })}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-content-muted mb-1">Available macros</div>
            <div className="flex flex-wrap gap-1.5">
              {POSTBACK_MACROS.map((m) => (
                <code key={m} className="text-[10px] px-1.5 py-0.5 bg-surface-muted rounded text-content-muted">{m}</code>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="secondary" onClick={cancel}>Cancel</Button>
            <Button type="button" onClick={save} loading={createMut.isPending || updateMut.isPending}>
              {editing === 'new' ? 'Add' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </Section>
  );
}

function PostbackLog({ postbackId }: { postbackId: string }) {
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['postback-log', postbackId],
    queryFn: () => getPostbackLog(postbackId, 20),
  });

  if (isLoading) return <div className="mt-3 text-xs text-content-subtle">Loading log…</div>;
  if (rows.length === 0) return <div className="mt-3 text-xs text-content-subtle">No deliveries yet.</div>;

  return (
    <div className="mt-3 border-t border-border pt-2">
      <div className="text-[11px] uppercase font-semibold text-content-subtle mb-1">Last {rows.length} deliveries</div>
      <div className="space-y-1">
        {rows.map((r: PostbackLogRow) => {
          const tone =
            r.status === 'success' ? 'bg-success-soft text-success' :
            r.status === 'failed'  ? 'bg-warning-soft text-warning' :
            r.status === 'dead'    ? 'bg-danger-soft text-danger' :
                                     'bg-surface-muted text-content-muted';
          return (
            <div key={r.id} className="text-[11px] font-mono flex items-center gap-2">
              <span className={`px-1.5 py-0.5 rounded uppercase font-semibold ${tone}`}>{r.status}</span>
              <span className="text-content-subtle shrink-0">{formatDateTime(r.last_attempt_at || r.created_at)}</span>
              <span className="text-content-muted shrink-0">attempts={r.attempts}</span>
              {r.last_status_code != null && <span className="text-content-muted shrink-0">HTTP {r.last_status_code}</span>}
              {r.last_error && <span className="text-danger truncate" title={r.last_error}>{r.last_error}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
